import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

// Base props without HTML button attributes
interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
  className?: string;
}

// Props when used as a button
interface ButtonAsButtonProps extends ButtonBaseProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> {
  as?: 'button';
}

// Props when used as another element (like Link)
interface ButtonAsOtherProps extends ButtonBaseProps {
  as: React.ElementType;
  to?: string;
  href?: string;
  [key: string]: any;
}

type ButtonProps = ButtonAsButtonProps | ButtonAsOtherProps;

/**
 * Button Component - Clinic Studios Design System
 *
 * Variants:
 * - primary: Sage green background, cream text (main CTAs)
 * - secondary: Outlined with sage green border (secondary actions)
 * - danger: Terracotta for destructive actions
 * - ghost: Transparent with hover state (subtle actions)
 *
 * Supports polymorphic rendering with `as` prop for use with react-router Link
 */
const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  children,
  className = '',
  as,
  ...props
}) => {
  const baseStyles = `
    inline-flex items-center justify-center
    font-body font-medium
    rounded-button
    transition-all duration-150 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2
    active:scale-[0.98]
    disabled:cursor-not-allowed disabled:active:scale-100
    no-underline
  `;

  const variants: Record<ButtonVariant, string> = {
    primary: `
      bg-clinic-primary text-clinic-cream shadow-clinic-sm
      hover:bg-clinic-primary-dark
      focus:ring-clinic-primary
      disabled:bg-clinic-muted disabled:text-white disabled:shadow-none
    `,
    secondary: `
      bg-transparent text-clinic-primary
      border-2 border-clinic-primary
      hover:bg-clinic-primary hover:text-clinic-cream
      focus:ring-clinic-primary
      disabled:border-clinic-muted disabled:text-clinic-muted disabled:hover:bg-transparent
    `,
    danger: `
      bg-clinic-accent text-white shadow-clinic-sm
      hover:opacity-90
      focus:ring-clinic-accent
      disabled:opacity-50 disabled:shadow-none
    `,
    ghost: `
      bg-transparent text-clinic-primary
      hover:bg-clinic-primary-lighter
      focus:ring-clinic-primary
      disabled:text-clinic-muted disabled:hover:bg-transparent
    `,
  };

  // Min-heights enforce accessible touch targets (md/lg meet the 44px guideline;
  // sm is for dense toolbars and stays tappable at 36px).
  const sizes: Record<ButtonSize, string> = {
    sm: 'px-3.5 py-2 text-sm gap-1.5 min-h-[36px]',
    md: 'px-5 py-2.5 text-base gap-2 min-h-[44px]',
    lg: 'px-7 py-3.5 text-lg gap-2.5 min-h-[52px]',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  const combinedClassName = `
    ${baseStyles}
    ${variants[variant]}
    ${sizes[size]}
    ${widthClass}
    ${className}
  `.replace(/\s+/g, ' ').trim();

  const content = isLoading ? (
    <>
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      Loading...
    </>
  ) : (
    <>
      {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
    </>
  );

  // Polymorphic rendering
  const Component = as || 'button';

  if (Component === 'button') {
    return (
      <button
        className={combinedClassName}
        disabled={(props as ButtonAsButtonProps).disabled || isLoading}
        {...(props as ButtonAsButtonProps)}
      >
        {content}
      </button>
    );
  }

  return (
    <Component className={combinedClassName} {...props}>
      {content}
    </Component>
  );
};

export default Button;
